export const AUTHORING_WRITING_GUIDE_VISUAL_SCALE_MIN = 0.25;
export const AUTHORING_WRITING_GUIDE_VISUAL_SCALE_SLIDER_MAX = 4;

export const AUTHORING_WRITING_GUIDE_VISUAL_DEFAULTS = Object.freeze({
  guideColor: '#E97132',
  guideStrokeScale: 1,
  pencilVisualScale: 1,
  showOriginalGuideOverlay: true,
});

export interface AuthoringWritingGuideStyle extends Record<string, unknown> {
  backgroundFillColor?: string;
  baseFillColor?: string;
  fillColor?: string;
  outlineColor?: string;
  guideColor?: string;
  guideStrokeScale?: number;
  pencilVisualScale?: number;
  showOriginalGuideOverlay?: boolean;
}

export type AuthoringWritingGuideVisualStyle = Required<
  Pick<
    AuthoringWritingGuideStyle,
    | 'guideColor'
    | 'guideStrokeScale'
    | 'pencilVisualScale'
    | 'showOriginalGuideOverlay'
  >
>;

const PERSISTED_COLOR_KEYS = [
  'backgroundFillColor',
  'baseFillColor',
  'fillColor',
  'outlineColor',
  'guideColor',
] as const;

export function normalizeAuthoringWritingGuideVisualStyle(
  value: unknown,
): AuthoringWritingGuideVisualStyle {
  const style = isRecord(value) ? value : {};
  return {
    guideColor: normalizeHexColor(
      style.guideColor,
      AUTHORING_WRITING_GUIDE_VISUAL_DEFAULTS.guideColor,
    ),
    guideStrokeScale: normalizeVisualScale(
      style.guideStrokeScale,
      AUTHORING_WRITING_GUIDE_VISUAL_DEFAULTS.guideStrokeScale,
    ),
    pencilVisualScale: normalizeVisualScale(
      style.pencilVisualScale,
      AUTHORING_WRITING_GUIDE_VISUAL_DEFAULTS.pencilVisualScale,
    ),
    showOriginalGuideOverlay:
      typeof style.showOriginalGuideOverlay === 'boolean'
        ? style.showOriginalGuideOverlay
        : AUTHORING_WRITING_GUIDE_VISUAL_DEFAULTS.showOriginalGuideOverlay,
  };
}

export function pickAuthoringWritingGuideStyleOverrides(
  value: unknown,
): AuthoringWritingGuideStyle {
  if (!isRecord(value)) return {};
  const picked: AuthoringWritingGuideStyle = {};

  for (const key of PERSISTED_COLOR_KEYS) {
    const color = normalizeOptionalHexColor(value[key]);
    if (color) Object.assign(picked, { [key]: color });
  }
  if (
    typeof value.guideStrokeScale === 'number' &&
    Number.isFinite(value.guideStrokeScale)
  ) {
    picked.guideStrokeScale = normalizeVisualScale(value.guideStrokeScale, 1);
  }
  if (
    typeof value.pencilVisualScale === 'number' &&
    Number.isFinite(value.pencilVisualScale)
  ) {
    picked.pencilVisualScale = normalizeVisualScale(value.pencilVisualScale, 1);
  }
  if (typeof value.showOriginalGuideOverlay === 'boolean') {
    picked.showOriginalGuideOverlay = value.showOriginalGuideOverlay;
  }

  return picked;
}

export function normalizeAuthoringWritingGuideHexColor(
  value: unknown,
  fallback: string,
): string {
  return normalizeHexColor(value, fallback);
}

export function normalizeAuthoringWritingGuideVisualScale(
  value: unknown,
  fallback = 1,
): number {
  return normalizeVisualScale(value, fallback);
}

function normalizeVisualScale(value: unknown, fallback: number): number {
  const numeric =
    typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  return Math.max(AUTHORING_WRITING_GUIDE_VISUAL_SCALE_MIN, numeric);
}

function normalizeHexColor(value: unknown, fallback: string): string {
  return normalizeOptionalHexColor(value) ?? fallback.toUpperCase();
}

function normalizeOptionalHexColor(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return /^#[0-9a-f]{6}$/i.test(normalized) ? normalized.toUpperCase() : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
